import { localPoint } from '@visx/event';

export const X_AXIS_FONT_SIZE = 12;
export const X_AXIS_GAP = 4;
export const X_AXIS_HEIGHT = X_AXIS_FONT_SIZE + X_AXIS_GAP;
export const X_AXIS_TICK_SPACING = 16;
export const MARKERS_OFFSET = 24;
export const MARKER_LINE_WIDTH = 2;
export const MARKER_FONT_SIZE = 12;
export const MARKER_PADDING = 4;
export const MARKER_HEIGHT = MARKER_FONT_SIZE + MARKER_PADDING * 2;
export const MARKERS_HEIGHT = 2 * MARKERS_OFFSET + MARKER_HEIGHT;

export const MARGIN_BOTTOM = MARKERS_HEIGHT;

export type MarkerType = 'SHARE_PRICE' | 'MY_FAIR_VALUE';

export const MARKER_COLORS_CSS_VARS: Record<MarkerType, string> = {
  SHARE_PRICE: 'var(--s-color-brand-01)',
  MY_FAIR_VALUE: 'var(--s-color-chart-03)',
};

/**
 * Measure the width of a text string using a canvas context.
 * This ensures cross-browser compatibility where some browsers might
 * load fonts asynchronously.
 * @param text - The text to measure.
 * @returns The width of the text in pixels.
 */
const measureMarkerWidth = (text: string) => {
  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 0;

    context.font = `${MARKER_FONT_SIZE}px Roboto, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif`;
    return context.measureText(text).width;
  } catch (e) {
    return 0;
  }
};

export const getMarkerBoxWidth = (label: string, value: string) => {
  const labelWidth = measureMarkerWidth(label);
  const valueWidth = measureMarkerWidth(value);
  const spaceWidth = measureMarkerWidth('-');
  const textWidth = labelWidth + valueWidth + spaceWidth;
  const startPadding = MARKER_LINE_WIDTH + MARKER_PADDING;
  const boxWidth = startPadding + MARKER_PADDING + textWidth;

  return {
    boxWidth,
    labelWidth,
    spaceWidth,
    startPadding,
  };
};

type Box = {
  x: number;
  width: number;
};

/**
 * Detects how much two boxes overlap based on their x-coordinate and width.
 * @param box1 - The first box with x and width properties.
 * @param box2 - The second box with x and width properties.
 * @returns The amount of overlap in pixels.
 */
export const measureBoxOverlap = (box1: Box, box2: Box): number => {
  const box1End = box1.x + box1.width;
  const box2End = box2.x + box2.width;
  // If no overlap, return 0
  if (box1.x >= box2End || box2.x >= box1End) {
    return 0;
  }

  // Calculate overlap amount by finding the minimum end point minus the maximum start point
  return Math.min(box1End, box2End) - Math.max(box1.x, box2.x);
};

export const getXPosition = (
  e: React.MouseEvent<SVGRectElement> | React.TouchEvent<SVGRectElement>,
  rect: DOMRect
) => {
  const point = localPoint(e);

  if (point?.x) return point.x;

  if ('touches' in e && e.touches.length > 0) {
    return e.touches[0].clientX - rect.x;
  }

  return 0;
};
